import { AxiosError } from 'axios';
import { BreadcrumbComponent, Button, CIActionGuard, CIPageHeader, ConfirmationPopup, Dropdown, Input, Loader, NoDataAvailable, Textarea, useToastMessage, useUserPermission } from 'ccs-ui-components';
import _, { debounce } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { requiredAuthPermission } from '../../../../common/constants/UserPermissionConstants';

import { exceptionDescMaxLength, exceptionNameMaxLength, regex } from '../../../../journey-builder/constants/JdRegex';
import { DEBOUNCE_DELAY, REGISTRY_MODE, URL_PATH } from '../../../constants';
import { ICheckValidExceptionName } from '../../../models';
import { AppDispatch, useSelector } from '../../../store/AppStore';
import useException from '../../../store/_api/exceptions/ExceptionChecker';
import { getCreateNewExceptionRequest } from '../../../store/actions/sagaActions/exceptions/CreateNewExceptionSagaAction';
import { getExceptionRequest } from '../../../store/actions/sagaActions/exceptions/GetExceptionSagaActions';
import { clearCreateNewException } from '../../../store/reducer/exceptions/GetCreateNewExceptionReducer';
import { DataLoadingState, exceptionValidation, viewHelperText } from '../TypeDefs';
import classes from './ExceptionWrapper.module.scss';

const ExceptionWrapper = () => {
  // state variables
  const [excName, setExcName] = useState('');
  const [excDesc, setExcDesc] = useState('');
  const [isSticky, setIsSticky] = useState(false);
  const [selectedException, setSelectedException] = useState<undefined | string>(undefined);
  const [invalidExceptionName, setInvalidExceptionName] = useState(exceptionValidation.VALID);
  const [invalidExceptionDesc, setInvalidExceptionDesc] = useState(exceptionValidation.VALID);
  const [apiResponseStatus, setApiResponseStatus] = useState<DataLoadingState>(
    DataLoadingState.COMPLETED
  );
  const [isExceptionNameValid, setIsExceptionNameValid] = useState<boolean | undefined>(undefined);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState<boolean>(true);
  const [notFoundExceptionForm, setNotFoundExceptionForm] = useState<boolean>(false);
  const [saveProcessing, setSaveProcessing] = useState<boolean>(false);

  // hook variables
  const { exceptionNameValidChecker } = useException();
  const { t: translate } = useTranslation();
  const toastMessage = useToastMessage();
  const history = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { permissionObject } = useUserPermission();
  const { state } = useLocation();
  const { exceptionName, version, mode } = useParams<{ exceptionName: string, version: string, mode: string }>();

  const { CreateNewExceptions, isLoading, errorMessage, success } = useSelector(
    (state) => state.GetCreateNewExceptionReducer
  );

  const { isLoading: exceptionReducerLoader, GetExceptions, success: fetchExceptionSuccess } = useSelector(
    (state) => state.GetExceptionReducer
  );

  const isCompView = () => {
    if (mode === REGISTRY_MODE.VIEW) {
      return true;
    }
    return false;
  };

  const isCompEdit = () => {
    if (mode === REGISTRY_MODE.EDIT) {
      return true;
    }
    return false;
  };

  const errorTextConstants = {
    exception_name_required: translate('create_exception.exception_name_required'),
    exception_name_invalid: translate('create_exception.exception_name_invalid'),
    exception_name_already_exists: translate('create_exception.exception_name_already_exists'),
    exception_description_invalid: translate('create_exception.exception_description_invalid'),
    exception_name_length_exceeded: translate('create_exception.exception_name_length_exceeded'),
    exception_description_length_exceeded: translate('create_exception.exception_description_length_exceeded'),
    network_fail: translate('event_validation_error.txt_varification_issue')
  };

  const helperText = {
    input_field: translate('create_exception.txt_input_field'),
    dropdown_lbl: translate('create_exception.txt_dropdown_lbl'),
    description_field: translate('create_exception.txt_description_field'),
  };

  const checkButtonDisable = () => {
    if (!selectedException) return true;
    if (isLoading) return true;
    if (saveProcessing) return true;
    if (excName === '') return true;
    if (apiResponseStatus !== DataLoadingState.COMPLETED) return true;
    if (invalidExceptionName === exceptionValidation.INVALID) return true;
    if (!isExceptionNameValid) return true;
    if (invalidExceptionName === exceptionValidation.LENGTH_EXCEEDED) return true;
    if (invalidExceptionDesc === exceptionValidation.LENGTH_EXCEEDED) return true;

    return false;
  };

  /**
   * @description disables the create exception button if any condition is true
   */
  const isButtonDisabled = checkButtonDisable();
  /**
   * Checks if the update button should be enabled or disabled
   */
  const isUpdatedButtonDisabled = useMemo(() => (
    !selectedException
    || isLoading
    || saveProcessing
    || [exceptionValidation.INVALID, exceptionValidation.LENGTH_EXCEEDED].includes(invalidExceptionDesc)
    || (excDesc === GetExceptions[0]?.ersExceptionDescription && selectedException.toLowerCase() === GetExceptions[0]?.ersExceptionType.toLowerCase())
  ), [selectedException, invalidExceptionDesc, excDesc, GetExceptions, isLoading]);

  /**
   * @method exceptionNameValidation
   * @description This function checks if the provided exception name is valid by making an API call.
   * If the API call is successful and the response data is not empty, the exception name is considered invalid.
   * The API response status is updated accordingly.
   */
  const exceptionNameValidation = () => {
    if (excName && invalidExceptionName === exceptionValidation.VALID) {
      setApiResponseStatus(DataLoadingState.LOADING);
      const existingException = exceptionNameValidChecker(excName);
      existingException.then((resp: { status: number; data: ICheckValidExceptionName[] | undefined }) => {
        if (resp.status === 200 && resp.data && resp.data?.length > 0) {
          setIsExceptionNameValid(false);
        }
        setApiResponseStatus(DataLoadingState.COMPLETED);
      }).catch((error: AxiosError) => {
        const err = error.response?.data as { errorCode: string; errorMessage: string; timeStamp: string };
        if (err?.errorMessage.includes(viewHelperText.error_404)) {
          setIsExceptionNameValid(true);
          setApiResponseStatus(DataLoadingState.COMPLETED);
        } else {
          setApiResponseStatus(DataLoadingState.FAILED);
        }
      });
    }
  };

  /**
   * @method nameChangeHandler
   * @description checks for exception name validation(must start with alphabets and must contain only alphabets, numbers and -)
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const nameChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length === 0) {
      setInvalidExceptionName(exceptionValidation.EMPTY);
    } else if (e.target.value.length > exceptionNameMaxLength) {
      setInvalidExceptionName(exceptionValidation.LENGTH_EXCEEDED);
      return;
    } else if (regex.name.test(e.target.value)) {
      setInvalidExceptionName(exceptionValidation.VALID);
    } else {
      setInvalidExceptionName(exceptionValidation.INVALID);
    }
    setIsExceptionNameValid(true);
    setExcName(e.target.value);
  };

  /**
   * @method descChangeHandler
   * @description checks for exception description validation(must start with alphabets)
   * @param {React.ChangeEvent<HTMLTextAreaElement>} e
   */
  const descChangeHandler = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length > exceptionDescMaxLength) {
      setInvalidExceptionDesc(exceptionValidation.LENGTH_EXCEEDED);
    } else if (regex.description.test(e.target.value)) {
      setInvalidExceptionDesc(exceptionValidation.VALID);
    }
    setExcDesc(e.target.value);
  };

  const callExceptionDetailsApi = debounce(() => {
    dispatch(getExceptionRequest(exceptionName));
  }, DEBOUNCE_DELAY);

  /**
   * @method handleCancelButton
   * @description handles cancel button
   */
  const handleCancelButton = () => {
    history(`${URL_PATH.CONTROL_PLANE}${URL_PATH.EXCEPTION_HOME}`, { state: { index: 0 } });
  };

  /**
  * @method viewException
  * @description Navigates to the exception table page and retains the applied filter.
  */
  const viewException = () => {
    const queryParam = state?.search;
    history(`${URL_PATH.CONTROL_PLANE}${URL_PATH.EXCEPTION_HOME}${queryParam || ''}`);
  };

  /**
   * @method editViewException
   * @description Navigates to the edit view exception page.
   * @param viewData - The data of the exception to be edited.
   */
  const editViewException = () => {
    callExceptionDetailsApi();
    history(`${URL_PATH.CONTROL_PLANE}${URL_PATH.EXCEPTION_EDIT}/${exceptionName}/${version}`);
  };

  /**
   * @method exceptionTypeChangeHandler
   * @description handles exception type
   * @param {React.ChangeEvent<HTMLSelectElement>} e
   */
  const exceptionTypeChangeHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedException(e.target.value);
  };

  /**
   * @method handleNewException
   * @description creates new exception by dispatching reducer function
   */
  const handleNewException = () => {
    setShowConfirmationPopup(false);
    setSaveProcessing(true);
    const payload = {
      ersExceptionDescription: excDesc,
      ersExceptionName: isCompEdit() ? exceptionName : excName,
      ersExceptionType: selectedException
    };
    if (isCompEdit() ? !isUpdatedButtonDisabled : !isButtonDisabled) {
      dispatch(getCreateNewExceptionRequest([payload]));
    }
  };

  /**
   * @method errorTextForExecName
   * @description Generates the error text based on the validation status of the exception name.
   * @returns {string} The error text corresponding to the validation status.
  */
  const errorTextForExecName = () => {
    if (invalidExceptionName === exceptionValidation.EMPTY) {
      return (errorTextConstants.exception_name_required);
    }
    if (invalidExceptionName === exceptionValidation.INVALID) {
      return (errorTextConstants.exception_name_invalid);
    }
    if (invalidExceptionName === exceptionValidation.LENGTH_EXCEEDED) {
      return (errorTextConstants.exception_name_length_exceeded);
    }
    if (isExceptionNameValid === false && invalidExceptionName === exceptionValidation.VALID) {
      return (errorTextConstants.exception_name_already_exists);
    }
    if (apiResponseStatus === DataLoadingState.FAILED) {
      return (errorTextConstants.network_fail);
    }
    return ('');
  };

  /**
   * @method errorTextForExecDesc
   * @description Generates the error text based on the validation status of the exception description.
   * @returns {string} The error text corresponding to the validation status.
   */
  const errorTextForExecDesc = () => {
    if (invalidExceptionDesc === exceptionValidation.LENGTH_EXCEEDED) {
      return (errorTextConstants.exception_description_length_exceeded);
    }
    return ('');
  };

  const noDataFragment = () => (
    <div className={classes['page-not-found-styling']}>
      <Button
        id="titleBody"
        variant="secondary"
        onClick={viewException}
        data-testid="btn-cancel"
        className={classes['back-btn']}
      >
        {translate('create_exception.lbl_back_btn')}
      </Button>
      <NoDataAvailable />
    </div>
  );

  const renderActionButtons = () => (
    (isCompView() && CIActionGuard([requiredAuthPermission.CP.edit], permissionObject.permission)) ? (
      <div className={`${isSticky ? classes['sticky-action-btn'] : classes['action-btn']} ci-button-group`}>
        <Button variant="secondary" className={classes['back-btn']} onClick={() => { viewException(); }} data-testid="backBtn">{translate('create_exception.lbl_back_btn')}</Button>
        <Button
          variant="primary"
          onClick={() => { editViewException(); }}
          data-testid="editBtn"
        >
          {translate('create_exception.lbl_edit_btn')}
        </Button>
      </div>
    ) : (
      <div className={`${isSticky ? classes['sticky-action-btn'] : classes['action-btn']} ci-button-group`}>
        <Button variant="secondary" className={classes['cancel-btn']} onClick={handleCancelButton}>
          {translate('create_exception.btn_cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleNewException}
          disabled={isCompEdit() ? isUpdatedButtonDisabled : isButtonDisabled}
        >
          {isCompEdit() ? translate('create_exception.btn_update') : translate('create_exception.btn_create')}
        </Button>
      </div>
    )
  );

  const renderHeadingText = () => {
    if (isCompView()) {
      return translate('create_exception.view_exception');
    }
    if (isCompEdit()) {
      return translate('create_exception.edit_exception');
    }
    return translate('create_exception.create_new_exception');
  };

  useEffect(() => {
    if (success) {
      if (isCompEdit()) {
        toastMessage.success(
          translate('create_exception.edit_title_success_header'),
          translate('create_exception.edit_msg_success_body')
        );
      } else {
        toastMessage.success(
          translate('create_exception.title_success_header'),
          translate('create_exception.msg_success_body')
        );
      }
      setTimeout(() => {
        history(`${URL_PATH.CONTROL_PLANE}${URL_PATH.EXCEPTION_VIEW}/${CreateNewExceptions[0]?.ersExceptionName}/${CreateNewExceptions[0]?.version}`);
      }, 200);
    }
    if (errorMessage) {
      if (isCompEdit()) {
        toastMessage.failure(
          translate('create_exception.edit_title_fail_header'),
          translate('create_exception.edit_msg_fail_body')
        );
      } else {
        toastMessage.failure(
          translate('create_exception.title_fail_header'),
          translate('create_exception.msg_fail_body')
        );
      }
    }
    setTimeout(() => {
      dispatch(clearCreateNewException());
      setSaveProcessing(false);
    }, 200);
  }, [success, errorMessage]);

  useEffect(() => {
    if (isCompView() || isCompEdit()) {
      callExceptionDetailsApi();
    }
    const windowScrollStickyHeader = () => {
      const sticky = 10;
      if (window.scrollY > sticky) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };
    window.addEventListener('scroll', windowScrollStickyHeader);

    return (() => {
      window.removeEventListener('scroll', windowScrollStickyHeader);
    });
  }, []);

  useEffect(() => {
    if (!_.isEmpty(GetExceptions) && (isCompView() || isCompEdit())) {
      setExcDesc(GetExceptions[0]?.ersExceptionDescription);
      setSelectedException(GetExceptions[0]?.ersExceptionType);
    }
  }, [GetExceptions]);

  useEffect(() => {
    if (mode !== undefined) {
      if ((isCompView() || isCompEdit())) {
        if (GetExceptions.length === 0 && fetchExceptionSuccess) {
          setNotFoundExceptionForm(true);
        } else {
          setNotFoundExceptionForm(false);
        }
      } else {
        setNotFoundExceptionForm(true);
      }
    } else {
      setNotFoundExceptionForm(false);
    }
  }, [GetExceptions, mode]);

  return (
    <div className="col-md-10 offset-md-1">
      {toastMessage.renderToast()}
      <BreadcrumbComponent isPreview={isCompView()} />
      <Loader show={isLoading || exceptionReducerLoader} />
      {!notFoundExceptionForm ? (
        <div>
          {!isCompView() && showConfirmationPopup && (<ConfirmationPopup message={String(translate('create_exception.txt_confirm_popup'))} />)}
          <div className={`${classes['exception-header-wrapper']} ${isSticky ? classes['sticky-header'] : ''}`}>
            <CIPageHeader header={renderHeadingText()} />
            {renderActionButtons()}
          </div>
          <div className={`form-wrapper-container ${classes['app-card']}`}>
            <div className={classes['exception-detail-head']}>{translate('create_exception.exception_details')}</div>
            <div className={classes['exception-detail']}>
              {translate('create_exception.exception_text')}
            </div>
            <div className={classes['exec-name']}>
              <Input
                labelText={String(translate('create_exception.lbl_exception_name'))}
                isMandatory
                helperText={helperText.input_field}
                error={(isCompView() || isCompEdit()) ? false : invalidExceptionName !== exceptionValidation.VALID || isExceptionNameValid === false || apiResponseStatus === DataLoadingState.FAILED}
                placeholder={String(translate('create_exception.exception_name_detail'))}
                value={(isCompView() || isCompEdit()) ? exceptionName : excName}
                data-testid="exception-name-input"
                onChange={nameChangeHandler}
                label=""
                name={String(translate('create_exception.lbl_exception_name'))}
                onBlur={exceptionNameValidation}
                errorTxt={errorTextForExecName()}
                isLoading={apiResponseStatus === DataLoadingState.LOADING}
                disabled={apiResponseStatus === DataLoadingState.LOADING || isCompEdit() || isCompView()}
                infoIconTxt={String(translate('create_exception.exception_name_info_bubble'))}
              />
            </div>
            <div className={classes['status-dropdown']}>
              <Dropdown
                labelText={String(translate('create_exception.lbl_exception_type'))}
                isMandatory
                label=""
                data={[
                  { value: 'Business', name: (translate('create_exception.lbl_business')) },
                  { value: 'System', name: (translate('create_exception.lbl_system')) },
                  { value: 'Data Quality', name: (translate('create_exception.lbl_data_quality')) }
                ]}
                className={classes['dropdown-control']}
                value={selectedException}
                onChange={exceptionTypeChangeHandler}
                placeholder={(isCompView() || isCompEdit()) ? _.startCase(GetExceptions[0]?.ersExceptionType?.toLowerCase()) : helperText.dropdown_lbl}
                data-testid="exception-dropdown"
                infoIconTxt={String(translate('create_exception.exception_type_info_bubble'))}
                disabled={isCompView()}
              />
            </div>
            <div className={classes['exec-desc']}>
              <Textarea
                labelText={String(translate('create_exception.txt_description'))}
                helperText={helperText.description_field}
                error={invalidExceptionDesc !== exceptionValidation.VALID}
                value={excDesc}
                placeholder={String(translate('create_exception.txt_provide_description'))}
                onChange={descChangeHandler}
                data-testid="exception-desc-input"
                label=""
                name="exceptionDesc"
                errorTxt={errorTextForExecDesc()}
                infoIconTxt={String(translate('create_exception.description_info_bubble'))}
                disabled={isCompView()}
              />
            </div>
          </div>
        </div>
      )
        : noDataFragment()}
    </div>
  );
};
export default ExceptionWrapper;
